import { ICartItem } from '../../../projects/shopping-cart/cart-bridger-pay/interfaces';
import {
    IUpsellExcludedProduct,
    IUpsellProgressBar,
    IUpsellRule,
    IUpsellTypeResult,
    UpsellReferrer
} from '../interfaces';
import { ProductDepartmentEnum } from '../../../projects/products/enums/product-department-enum';
import { round2DecimalPlaces } from '../../math-provider';
import ProductPriceFormat from '../../../projects/products/product-price/product-price-format';
import React from 'react';

/**
 * $ - Cart Total Fixed
 * For $x amount get $y discount
 */
export class UpsellType4 {

    private readonly _cartItems: ICartItem[];
    private readonly _upsellRules: IUpsellRule[];
    private _upsellExcludedProducts: IUpsellExcludedProduct[];
    private readonly _referrer: UpsellReferrer;
    private readonly _totalUpsellDiscount: number;

    private readonly _upsellType = 4;

    constructor(
        cartItems: ICartItem[],
        upsellRules: IUpsellRule[],
        upsellExcludedProducts: IUpsellExcludedProduct[],
        referrer: UpsellReferrer,
        totalUpsellDiscount: number
    ) {
        this._cartItems = cartItems;
        this._referrer = referrer;
        this._totalUpsellDiscount = totalUpsellDiscount;

        this._upsellRules = [...upsellRules];
        this._upsellRules.sort((rule1, rule2) => rule2.pluginQuantity - rule1.pluginQuantity);

        this._upsellExcludedProducts = upsellExcludedProducts;
    }

    /**
     * "Add $19.01 to get $40 off"
     */
    public getUpsellTypeResult() : IUpsellTypeResult {

        try{
            const { rule, total, isAboveMaxTier, productsCount } = this.getNextActiveRule();
            if(!rule) {
                return {
                    isVisible: isAboveMaxTier,
                    isAboveMaxTier,
                    upsellType: this._upsellType,
                    productsCount,
                };
            }

            // "Add $19.01 to get $40 off"
            const addAmount = Math.abs(total - rule.pluginQuantity);
            // console.log(`rule.pluginQuantity - total = ${ rule.pluginQuantity } - ${ total } = ${ addAmount }`);

            const percent = total * 100 / rule.pluginQuantity;
            const percentRounded = round2DecimalPlaces(percent);
            const discount = rule.value;

            const progressBar: IUpsellProgressBar = {
                min: 0,
                max: rule.pluginQuantity,
                value: addAmount,
                percent: percentRounded,
                ariaText: `Add $${ addAmount } to get $${ discount } off`,
                userValue: total,
            };

            const text = (
                <div>
                    Add <ProductPriceFormat price={ addAmount } /> to get <strong><ProductPriceFormat price={ discount } /> off</strong>
                </div>
            );

            return {
                isVisible: true,
                add: addAmount,
                discount,
                progressBar,
                isAboveMaxTier,
                upsellType: this._upsellType,
                text,
                productsCount,
            }
        }
        catch(error) {
            console.error(error);
            return {
                isVisible: false,
                isAboveMaxTier: false,
                upsellType: this._upsellType,
                productsCount: 0,
            };
        }
    }

    private getNextActiveRule = () : {
        rule: IUpsellRule|null;
        total: number;
        isAboveMaxTier: boolean;
        productsCount: number;
    } => {
        const filteredCartItems = this.filterNonUpsellProducts();

        const total = this.getTotal(filteredCartItems);

        if(total <= 0) {
            return {
                rule: null,
                total,
                isAboveMaxTier: false,
                productsCount: filteredCartItems.length,
            }
        }

        const hasUpsellRules = this._upsellRules.length > 0;
        const instantSavingsShown = this._totalUpsellDiscount > 0;

        if(this._referrer === UpsellReferrer.CartStep3) {
            return {
                rule: null,
                isAboveMaxTier: hasUpsellRules && instantSavingsShown,
                total,
                productsCount: filteredCartItems.length,
            };
        }

        const nextActiveRule = this.getNextActiveRuleByTotal(total);
        const isAboveMaxTier = !nextActiveRule && hasUpsellRules;

        return {
            rule: nextActiveRule,
            total,
            isAboveMaxTier,
            productsCount: filteredCartItems.length,
        };
    };

    private getTotal = (filteredCartItems: ICartItem[]) : number => {
        let total = 0;
        for (const upsellCartItem of filteredCartItems)
        {
            total += upsellCartItem.skuPrice * upsellCartItem.skuUnits;
        }

        return total;
    };

    private getNextActiveRuleByTotal = (total: number) : IUpsellRule|null => {
        let selectedRule: IUpsellRule|null = null;

        for(const rule of this._upsellRules) {
            if (total < rule.pluginQuantity)
            {
                selectedRule = rule;
            }
        }

        return selectedRule;
    };

    private filterNonUpsellProducts(): ICartItem[] {

        const filtered: ICartItem[] = [];

        for(const cartItem of this._cartItems) {

            if(!cartItem.isActive ||
                !cartItem.skuEnabled ||
                cartItem.skuPrice <= 0) continue;

            if(cartItem.skuDepartmentID !== ProductDepartmentEnum.Bundles &&
                cartItem.skuDepartmentID !== ProductDepartmentEnum.Plugins) continue;

            const isExcluded = this._upsellExcludedProducts.find(item => item.skuid === cartItem.skuid);
            if(isExcluded) {
                // console.log(`${ cartItem.skuName } - excluded`);
                continue;
            }

            filtered.push(cartItem);
        }

        return filtered;
    }
}